import PouchDB from 'pouchdb-browser';
import { getDecoded, getJwt, refresh } from '@/lib/auth';
import { resolveConflicts } from '@/lib/conflicts';

// Before the per-login split every account shared this one local replica, so
// a browser that switched between tenants without a wipe carried one tenant's
// data into the other (client 19 Oct, "tenant_id immutable"). Kept only to
// carry over its unsent edits once (legacy-local.ts) and then delete it.
export const LEGACY_LOCAL_DB_NAME = 'ft-local';

// FNV-1a: short, stable and safe in an IndexedDB name, so the email itself is
// not spelled out in the browser's storage list.
function shortHash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** One local replica per tenant + login, so switching accounts in a browser
 *  never mixes two tenants' data and never strands another login's unsent
 *  edits. A technician limited to their customers (client #92) gets a
 *  separate one, so the full copy from before can be retired cleanly. */
export function localDbNameFor(
  tenantId: string | undefined,
  sub: string | undefined,
  scoped = false
): string {
  if (!tenantId || !sub) return 'ft-signed-out';
  return `ft-${tenantId}-${shortHash(sub)}${scoped ? '-a' : ''}`;
}

/** The session only receives its calendar customers and syncs through the
 *  API's scope proxy, not CouchDB directly (client #92). */
export function isScopedSession(): boolean {
  return getDecoded()?.sync_scope === 'assigned';
}

function currentLocalDbName(): string {
  const d = getDecoded();
  return localDbNameFor(d?.tenant_id, d?.sub, isScopedSession());
}

let localDb: PouchDB.Database | null = null;
let localDbName: string | null = null;
let remoteDb: PouchDB.Database | null = null;
let remoteDbUrl: string | null = null;
interface ActiveSync {
  cancel: () => void;
}
let activeSync: ActiveSync | null = null;

function couchBase(): string {
  const fromEnv = import.meta.env.VITE_COUCH_URL as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/data$/, '');
  return '/couch';
}

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';
}

function remoteUrl(): string | null {
  const decoded = getDecoded();
  const tenantId = decoded?.tenant_id;
  if (!tenantId) return null;
  if (isScopedSession()) return `${apiBase()}/sync/data_${tenantId}`;
  return `${couchBase()}/data_${tenantId}`;
}

export function getPouchDB(): PouchDB.Database {
  const name = currentLocalDbName();
  if (localDb && localDbName !== name) {
    // The login or tenant changed under us: stop syncing the previous owner's
    // replica and leave it on disk (its unsent edits wait for that login).
    releaseLocal();
  }
  if (!localDb) {
    localDb = new PouchDB(name);
    localDbName = name;
  }
  return localDb;
}

/** Stop sync and drop the handles without deleting anything, so the next
 *  getPouchDB()/getRemoteDB() open the current owner's replica and tenant. */
export function releaseLocal(): void {
  stopSync();
  resetLocalDbHandle();
  remoteDb = null;
  remoteDbUrl = null;
}

// Drop the cached local handle so the next getPouchDB() reopens a fresh
// connection to the SAME on-disk data. Used to recover from an
// "InvalidStateError: database connection is closing" when a destroy/recreate
// raced an in-flight write (the data is intact; only the handle was stale).
export function resetLocalDbHandle(): void {
  if (localDb) {
    try {
      void localDb.close();
    } catch {
      /* ignore */
    }
  }
  localDb = null;
  localDbName = null;
}

const authedFetch: typeof fetch = async (input, init) => {
  const headers = new Headers(init?.headers ?? {});
  const jwt = getJwt();
  if (jwt && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${jwt}`);
  }
  const doIt = () =>
    PouchDB.fetch(input as RequestInfo, { ...(init ?? {}), headers });
  let res = await doIt();
  if (res.status === 401) {
    try {
      const newJwt = await refresh();
      headers.set('authorization', `Bearer ${newJwt}`);
      res = await PouchDB.fetch(input as RequestInfo, {
        ...(init ?? {}),
        headers,
      });
    } catch {
      // Do NOT clear the session here. refresh() already clears tokens on a
      // genuine server-side auth rejection (its own !res.ok path). Reaching
      // this catch means refresh threw WITHOUT rejecting credentials — almost
      // always because the network dropped mid-flight (offline). A couch 401
      // can coincide with the offline window, forcing a refresh whose fetch
      // fails with ERR_INTERNET_DISCONNECTED; logging the user out for that
      // transient blip breaks offline-first use. Keep the session and return
      // the original 401 — the retrying live replication recovers on reconnect.
    }
  }
  return res;
};

export function getRemoteDB(): PouchDB.Database | null {
  const url = remoteUrl();
  if (!url) return null;
  if (!remoteDb || remoteDbUrl !== url) {
    remoteDb = new PouchDB(url, {
      fetch: authedFetch,
      skip_setup: true,
    } as PouchDB.Configuration.RemoteDatabaseConfiguration);
    remoteDbUrl = url;
  }
  return remoteDb;
}

export interface SyncHandle {
  stop: () => void;
}

// Injected by the store so this module stays free of store imports. Called when
// the server rejects a replicated doc (validate_doc_update `denied`) — a write
// that violated a role rule or a conflict that never reached the server.
let replicationErrorHandler: ((info: unknown) => void) | null = null;
export function setReplicationErrorHandler(h: (info: unknown) => void): void {
  replicationErrorHandler = h;
}

// Injected by the store to drive the `online` flag from real replication state
// (active = reaching the server, paused-with-error = cannot reach it).
let onlineHandler: ((online: boolean) => void) | null = null;
export function setOnlineHandler(h: (online: boolean) => void): void {
  onlineHandler = h;
}

// Injected by the store to drive the first-sync progress UI. `target` is the
// remote doc_count (null until known); `loaded` is the local doc_count so far.
// Best-effort: reads are swallowed on error so this never breaks replication.
let syncProgressHandler:
  | ((loaded: number, target: number | null) => void)
  | null = null;
export function setSyncProgressHandler(
  h: (loaded: number, target: number | null) => void
): void {
  syncProgressHandler = h;
}

// What replication is doing right now, so the sidebar can tell the technician
// whether data is still coming down or their own work is still going up
// (client #68 — "see what data is only on my device / on the server /
// downloading").
export enum SyncActivity {
  IDLE = 'idle',
  DOWNLOADING = 'downloading',
  UPLOADING = 'uploading',
}
let syncActivityHandler: ((a: SyncActivity) => void) | null = null;
export function setSyncActivityHandler(h: (a: SyncActivity) => void): void {
  syncActivityHandler = h;
}

export function startSync(): SyncHandle {
  if (activeSync) {
    return { stop: () => stopSync() };
  }
  const local = getPouchDB();
  const remote = getRemoteDB();
  if (!remote) {
    return { stop: () => stopSync() };
  }
  const d = getDecoded();
  if (isScopedSession()) {
    // The full copy this login held before it was limited goes, after its
    // unsent edits are pushed (client #92: other customers must not stay on
    // the device).
    void retireReplica(localDbNameFor(d?.tenant_id, d?.sub, false), remote);
    activeSync = startScopedSync(remote);
  } else {
    void retireReplica(localDbNameFor(d?.tenant_id, d?.sub, true), remote);
    // Per-tenant DB: no filter needed — _security on data_<tenant> enforces
    // read isolation natively. Both pull and push are straight live + retry.
    activeSync = wireReplication(
      local,
      remote,
      local.replicate.from(remote, { live: true, retry: true }),
      local.replicate.to(remote, { live: true, retry: true })
    );
  }
  return { stop: () => stopSync() };
}

type Replication = PouchDB.Replication.Replication<object>;

function wireReplication(
  local: PouchDB.Database,
  remote: PouchDB.Database,
  pull: Replication,
  push: Replication
): ActiveSync {
  // `denied` = a doc the server refused (validate_doc_update / auth) — surface
  // it. `error` is mostly transient connectivity that retry already handles, so
  // it is only logged to avoid toast spam while offline.
  const onDenied = (info: unknown): void => {
    console.error('[sync] replication denied:', info);
    if (replicationErrorHandler) replicationErrorHandler(info);
  };
  const onError = (err: unknown): void => {
    console.error('[sync] replication error:', err);
  };
  const markOnline = (v: boolean): void => {
    if (onlineHandler) onlineHandler(v);
  };

  // Downloading takes precedence in the report: an incoming batch is what the
  // user is waiting to see, an outgoing one is already safe in the local DB.
  let pullBusy = false;
  let pushBusy = false;
  const reportActivity = (): void => {
    if (!syncActivityHandler) return;
    syncActivityHandler(
      pullBusy
        ? SyncActivity.DOWNLOADING
        : pushBusy
          ? SyncActivity.UPLOADING
          : SyncActivity.IDLE
    );
  };

  // First-sync progress. The remote doc_count is the target; the local
  // doc_count is what has replicated so far. Both are best-effort — errors are
  // swallowed so a failing .info() never disrupts replication.
  let syncTarget: number | null = null;
  const reportProgress = (): void => {
    if (!syncProgressHandler) return;
    local
      .info()
      .then(i => {
        if (syncProgressHandler) syncProgressHandler(i.doc_count, syncTarget);
      })
      .catch(() => {
        /* best-effort */
      });
  };
  // Fire-and-forget: learn the target doc count from the remote DB.
  remote
    .info()
    .then(i => {
      syncTarget = i.doc_count;
      if (syncProgressHandler) syncProgressHandler(0, syncTarget);
      reportProgress();
    })
    .catch(() => {
      /* offline / no count — screen falls through after its timeout */
    });

  // Converge conflicted docs shortly after a pull settles. Debounced so a burst
  // of incoming changes triggers a single pass.
  let resolveTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleResolve = (): void => {
    if (resolveTimer) return;
    resolveTimer = setTimeout(() => {
      resolveTimer = null;
      void resolveConflicts(local).catch(err =>
        console.error('[sync] conflict resolve failed:', err)
      );
    }, 500);
  };

  pull
    .on('denied', onDenied)
    .on('error', onError)
    .on('change', () => {
      scheduleResolve();
      reportProgress();
    })
    .on('active', () => {
      markOnline(true);
      pullBusy = true;
      reportActivity();
    })
    .on('paused', (err: unknown) => {
      markOnline(!err);
      pullBusy = false;
      reportActivity();
      if (!err) reportProgress();
    });
  push
    .on('denied', onDenied)
    .on('error', onError)
    .on('active', () => {
      markOnline(true);
      pushBusy = true;
      reportActivity();
    })
    .on('paused', (err: unknown) => {
      markOnline(!err);
      pushBusy = false;
      reportActivity();
    });

  return {
    cancel: () => {
      if (resolveTimer) clearTimeout(resolveTimer);
      try {
        pull.cancel();
      } catch {
        /* ignore */
      }
      try {
        push.cancel();
      } catch {
        /* ignore */
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Technician scope (client #92)
// ---------------------------------------------------------------------------

/** How often a limited session asks which customers it may hold. A new
 *  appointment shows up, and a past one disappears, within this. */
const SCOPE_POLL_MS = 5 * 60 * 1000;

export interface SyncScope {
  version: string;
  ids: string[];
}

// Injected by AuthGate: the local replica was deleted and recreated, so the
// store must start over from it.
let localReplacedHandler: (() => void) | null = null;
export function setLocalReplacedHandler(h: (() => void) | null): void {
  localReplacedHandler = h;
}

function scopeCacheKey(): string {
  return `ft-scope:${currentLocalDbName()}`;
}

function readCachedScope(): SyncScope | null {
  try {
    const raw = localStorage.getItem(scopeCacheKey());
    return raw ? (JSON.parse(raw) as SyncScope) : null;
  } catch {
    return null;
  }
}

function writeCachedScope(s: SyncScope): void {
  try {
    localStorage.setItem(scopeCacheKey(), JSON.stringify(s));
  } catch {
    /* per-device convenience only */
  }
}

async function fetchScope(): Promise<SyncScope> {
  const url = remoteUrl();
  if (!url) throw new Error('no session');
  const res = await authedFetch(`${url}/_scope`, { method: 'GET' });
  if (!res.ok) throw new Error(`scope: HTTP ${res.status}`);
  return (await res.json()) as SyncScope;
}

/** Ids in the local replica that the scope no longer covers. Docs created
 *  here and not yet pushed are not counted: the caller pushes first. */
export function outOfScope(localIds: string[], scope: SyncScope): string[] {
  const allowed = new Set(scope.ids);
  return localIds.filter(id => !id.startsWith('_design/') && !allowed.has(id));
}

/** Push what the replica holds, then delete it and open an empty one, so
 *  customers that left the scope leave the device. Throws (and keeps the
 *  replica) when the push cannot finish, e.g. offline. */
async function rebuildLocal(remote: PouchDB.Database): Promise<void> {
  const local = getPouchDB();
  await local.replicate.to(remote);
  const name = localDbName ?? currentLocalDbName();
  resetLocalDbHandle();
  await new PouchDB(name).destroy();
  getPouchDB();
  if (localReplacedHandler) localReplacedHandler();
}

/** Delete another replica of this login (the other scope mode) after pushing
 *  its unsent edits. Kept when the push fails; a later start retries. */
async function retireReplica(
  name: string,
  remote: PouchDB.Database
): Promise<void> {
  if (name === 'ft-signed-out' || !(await replicaExists(name))) return;
  const old = new PouchDB(name);
  try {
    await old.replicate.to(remote);
    await old.destroy();
  } catch (err) {
    console.warn('[sync] keeping previous replica for now:', err);
    try {
      await old.close();
    } catch {
      /* ignore */
    }
  }
}

/** Whether a local replica of that name exists, without creating it. */
export async function replicaExists(name: string): Promise<boolean> {
  const idb = globalThis.indexedDB as
    | (IDBFactory & { databases?: () => Promise<{ name?: string }[]> })
    | undefined;
  if (!idb) return false;
  if (typeof idb.databases === 'function') {
    const dbs = await idb.databases();
    return dbs.some(d => d.name === `_pouch_${name}`);
  }
  // No listing API: opening creates an empty DB, which says "nothing there".
  const db = new PouchDB(name);
  const info = await db.info();
  if (info.doc_count === 0) {
    await db.destroy();
    return false;
  }
  await db.close();
  return true;
}

/** Pull only the scope's docs, push everything, and follow scope changes:
 *  a grown scope restarts the pull (a new doc_ids list is a new checkpoint,
 *  so older docs of a newly assigned customer arrive too); a shrunk one
 *  rebuilds the replica. Offline, the last known scope keeps sync going. */
function startScopedSync(remote: PouchDB.Database): ActiveSync {
  let cancelled = false;
  let current: ActiveSync | null = null;
  let scope: SyncScope | null = null;
  let running = false;
  // A rebuild that could not finish (offline) is retried on the next poll
  // even when the scope itself has not changed.
  let rebuildPending = false;

  const wire = (s: SyncScope | null): void => {
    current?.cancel();
    const local = getPouchDB();
    const push = local.replicate.to(remote, { live: true, retry: true });
    const pull = s
      ? local.replicate.from(remote, {
          live: true,
          retry: true,
          doc_ids: s.ids,
        })
      : null;
    current = pull
      ? wireReplication(local, remote, pull, push)
      : {
          cancel: () => {
            try {
              push.cancel();
            } catch {
              /* ignore */
            }
          },
        };
  };

  const refresh = async (): Promise<void> => {
    if (running || cancelled) return;
    running = true;
    try {
      let next: SyncScope;
      try {
        next = await fetchScope();
      } catch (err) {
        // Offline or the server is away: keep going with what we knew.
        if (!current) wire(scope ?? readCachedScope());
        console.warn('[sync] scope unavailable:', err);
        return;
      }
      if (
        cancelled ||
        (current && !rebuildPending && next.version === scope?.version)
      )
        return;
      const local = getPouchDB();
      const ids = (await local.allDocs()).rows.map(r => r.id);
      if (outOfScope(ids, next).length > 0) {
        current?.cancel();
        current = null;
        try {
          await rebuildLocal(remote);
          rebuildPending = false;
          // Pushing may have created docs the scope now includes.
          next = await fetchScope();
        } catch (err) {
          rebuildPending = true;
          console.warn('[sync] scope rebuild postponed:', err);
        }
      }
      if (cancelled) return;
      scope = next;
      writeCachedScope(next);
      wire(next);
    } finally {
      running = false;
    }
  };

  void refresh();
  const timer = setInterval(() => void refresh(), SCOPE_POLL_MS);
  return {
    cancel: () => {
      cancelled = true;
      clearInterval(timer);
      current?.cancel();
      current = null;
    },
  };
}

function stopSync(): void {
  if (activeSync) {
    try {
      activeSync.cancel();
    } catch {
      // ignore
    }
    activeSync = null;
  }
}

/** Delete the signed-in login's replica (and the pre-split shared one) on
 *  logout, so the next person on a shared device sees nothing of it. Works
 *  whether or not the handle is open in this page. */
export async function destroyLocal(): Promise<void> {
  const name = localDbName ?? currentLocalDbName();
  releaseLocal();
  for (const n of [name, LEGACY_LOCAL_DB_NAME]) {
    try {
      await new PouchDB(n).destroy();
    } catch {
      // ignore
    }
  }
}
